import React from 'react'
import { createUseStyles } from '../helpers/createStyles'
import PropTypes from 'prop-types'
import ResponsiveImage from './ResponsiveImage'
import { theme } from '../styles/theme'
import RichContent from './RichContent'
import Pill from './Pill'
import LetterMark from '../icons/lettermark.svg'
import cn from 'classnames'
import Link from './Link'
import { parseAssetId, buildFileUrl } from '@sanity/asset-utils'
import { useRouter } from 'next/router'
function clampText (text, length) {
  // Text can be a string or an array of objects
  // [
  //   {
  //     "children": [
  //         {
  //             "_type": "span",
  //             "marks": [],
  //             "text": "High-pile threads and using soft wool and linen blends. Hard wearing.",
  //             "_key": "74ba066944740"
  //         }
  //     ],
  //     "_type": "block",
  //     "style": "normal",
  //     "_key": "245bcf45b424",
  //     "markDefs": []
  // }
  // ]
  if (Array.isArray(text)) {
    return text.map(block => ({
      ...block,
      children: block.children.map(child => ({
        ...child,
        text: child.text.length > length ? child.text.substring(0, length) + '...' : child.text
      }))
    }))
  }

  if (!text) return ''
  if (!length) return text
  if (length < 0) return ''
  if (length > text.length) return text
  return text.substring(0, length) + '...'
}

const Card = ({ cardContent, preferHexCode, isFocused, hideFooter, isMobile, hideViewProductButton, hideBackground, hideForeground, hideFooterTitle, disableHover, forceDesktopMode, aspectRatio = '45 / 55', className, onClick, rightAlignFooter = false }) => {
  const classes = useStyles()
  const router = useRouter()

  const title = cardContent.title || cardContent.name
  const description = clampText(cardContent.description, 200)
  const hexCode = cardContent.hexCode
  const image = cardContent.productImage || cardContent.image
  const composition = clampText(cardContent.composition, 100)
  const suitability = clampText(cardContent.suitability, 100)
  const isCommerciallyGraded = cardContent.isCommerciallyGraded
  const material = clampText(cardContent.material, 100)
  const length = clampText(cardContent.length, 100)
  const type = cardContent._type
  const isProduct = type === 'tepProduct'
  const isFinish = type === 'tepProductFinish'

  function navigateToProduct (url) {
    router.push(url, null, { shallow: false })
  }

  // Extract the spec sheet URL from the cardContent
  let parts, specSheetURL
  if (cardContent.productSpecSheet) {
    const assetRef = cardContent.productSpecSheet.asset?._ref
    if (!assetRef) {
      return null
    }
    parts = parseAssetId(assetRef)
    specSheetURL = buildFileUrl(parts, { projectId: process.env.SANITY_PROJECT_ID, dataset: process.env.SANITY_PROJECT_DATASET })
  }

  // const parts = parseAssetId(_file_.asset._ref);
  // const url = buildFileUrl(parts, options);

  let backgroundTitle = ''
  if (isProduct) {
    backgroundTitle = 'WEAVE'
  }
  if (isFinish) {
    backgroundTitle = 'FINISHING'
  }

  return (
    <div
      className={cn(
        classes.card,
        className,
        disableHover && 'disableHover',
        forceDesktopMode && 'forceDesktopMode'
      )}
      onClick={() => {
        if (isMobile && cardContent.slug) {
          navigateToProduct(`/trade-exclusive-product?product-id=${cardContent._id}`)
        }
      }}
    >
      <div
        className={cn(classes.cardForeground, 'cardForeground', hideForeground && 'hideForeground')}
        onClick={() => onClick && !(isMobile && cardContent.slug) && onClick('foreground')}
      >
        {
          (hexCode && preferHexCode) && (
            <div className={cn(classes.colorBlock, 'hexCode')} style={{ backgroundColor: hexCode }} />
          )
        }
        {((!hexCode || !preferHexCode) && image) && (
          <ResponsiveImage
            image={image || cardContent.image} className={cn(classes.imageContainer, 'imageContainer')} style={{
              aspectRatio: isMobile ? '70 / 100' : aspectRatio
            }}
          />
        )}
        {(!hexCode && !image) && (
          <div className={classes.imageContainer}>
            <div className={classes.imagePlaceholder}>
              <p>No Image Available</p>
            </div>
          </div>
        )}
      </div>
      {((isProduct || isFinish) && !hideBackground) && (
        <div
          className={cn(classes.cardBackground, 'cardBackground', hideForeground && 'foregroundHidden')}
          style={{
            opacity: isFocused ? 1 : undefined
          }}
          onClick={() => onClick && onClick('background')}
        >
          <div className={cn(classes.cardBackgroundHeader, 'cardBackgroundHeader')}>
            <h3>NAME OF {backgroundTitle}</h3>
            <h4>{title}</h4>
            <RichContent content={description} />
          </div>

          <div className={cn(classes.cardBackgroundFooter, 'cardBackgroundFooter')}>
            {composition && (
              <div>
                <h3>COMPOSITION</h3>
                <RichContent content={composition} />
              </div>
            )}

            {suitability && (
              <div>
                <h3>SUITABILITY</h3>
                <RichContent content={suitability} />
              </div>
            )}

            {isCommerciallyGraded && (
              <div>
                <h3 />
                <Pill text='COMMERCIALLY GRADED' />
              </div>
            )}

            {material && (
              <div>
                <h3>MATERIAL</h3>
                <RichContent content={material} />
              </div>
            )}

            {length && (
              <div>
                <h3>LENGTH</h3>
                <RichContent content={length} />
              </div>
            )}
            <LetterMark className={classes.letterMarkSvg} />
          </div>
        </div>
      )}
      {!hideFooter && (
        <div className={cn(classes.cardFooter, 'cardFooter', rightAlignFooter ? 'rightAlignFooter' : '')}>
          {(title && !hideFooterTitle) && (
            <p className={classes.productName}>{title}</p>
          )}
          <div className={cn(classes.cardFooterLinkWrapper)}>
            {(specSheetURL && !isMobile) && (
              <a href={specSheetURL} target='_blank' rel='noopener noreferrer'>
                DOWNLOAD SPEC SHEET
              </a>
            )}
            {(!hideViewProductButton && !isMobile && cardContent.slug) && (
              <Link to={`/trade-exclusive-product?product-id=${cardContent._id}`} showExternalIcon={false} forceBlankTarget>VIEW PRODUCT</Link>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'space-between',
    // borderRadius: 8,
    overflow: 'visible',
    // backgroundColor: "#fff",
    // boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    transition: 'transform 0.2s ease-in-out',
    maxHeight: '188px',
    height: 'fit-content',
    minHeight: 'fit-content !important',
    gap: theme.spacing(0),
    // gap: theme.spacing(16),
    [theme.breakpoints.up('md')]: {
      minWidth: '453px',
      maxWidth: '453px',
      minHeight: '551px',
      maxHeight: 'fit-content',
      gap: theme.spacing(0)
    },
    '&.forceDesktopMode': {
      minWidth: '453px',
      maxWidth: '453px',
      minHeight: '551px',
      maxHeight: 'fit-content',
      gap: theme.spacing(0)
    },
    '&.overflow': {
      overflow: 'visible'
    }
  },
  cardForeground: {
    '&.hideForeground': {
      visibility: 'hidden',
      pointerEvents: 'none',
      position: 'absolute'
    }
  },
  cardBackground: {
    width: '100%',
    // aspectRatio: '45 / 55',
    // aspectRatio: isMobile ? '70 / 100' : aspectRatio
    maxWidth: '100%',

    position: 'absolute',
    backgroundColor: '#C7C4B4',
    zIndex: 4,
    opacity: 0,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(5),
    transition: 'opacity 0.2s ease-in-out',
    '&:hover, &:focus, &:active': {
      opacity: 1
    },
    '&.foregroundHidden': {
      position: 'relative'
    }
  },
  cardBackgroundHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    gap: theme.spacing(2),
    '& h3': {
      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 12,
      color: theme.colors.black,
      opacity: 0.3,
      marginBottom: theme.spacing(0)
    },
    '& h4': {
      fontFamily: 'Iskry',
      fontWeight: 700,
      fontSize: 40,
      color: theme.colors.black,
      marginBottom: theme.spacing(1)
    },
    '& p': {
      fontFamily: 'Sohne',
      fontWeight: 300,
      fontSize: 14,
      color: theme.colors.black
    }
  },
  cardBackgroundFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    gap: theme.spacing(2),
    '& > div': {
      width: '100%'
    },
    '& h3': {
      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 10,
      color: theme.colors.black,
      opacity: 0.3,
      marginBottom: theme.spacing(0),
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      borderTop: '1px solid #000',
      width: '100%'
    },
    '& p': {
      fontFamily: 'Sohne',
      fontWeight: 300,
      fontSize: 12,
      color: theme.colors.black,
      paddingRight: 50
    }
  },
  imageContainer: {
    height: '100%',
    width: '100%',
    // aspectRatio: '45 / 55',
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    justifyContent: 'stretch',
    img: {
      // position: "static",
      // objectFit: "cover",
      width: '100%',
      // height: "100%",
      // width: 'fit-content',
      height: 'fit-content'
    },
    ' & > div': {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      // color: "#ccc",
      backgroundColor: 'none',
      flexGrow: 1
    }
  },

  cardFooter: {
    fontFamily: 'Sohne',
    fontWeight: 400,
    fontSize: 12,
    textAlign: 'center',
    color: theme.colors.black,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,

    '& p': {
      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 12
    },

    '& a': {
      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 12,
      textDecoration: 'underline'
    },

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(2)
    },

    '&.rightAlignFooter': {
      marginLeft: 'auto'
    }
  },
  cardFooterLinkWrapper: {
    display: 'flex',
    gap: '32px'
  },
  productName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 0,
    color: '#333'
  },
  letterMarkSvg: {
    width: '32px',
    position: 'absolute',
    bottom: 0,
    right: 0,
    marginRight: theme.spacing(5),
    marginBottom: theme.spacing(5),
    // display: 'block',
    // gridColumn: '1 / span 4',
    [theme.breakpoints.down('sm')]: {
      // gridColumn: '1 / -1',
      // justifySelf: 'center',
      // marginBottom: theme.spacing(7)
    }
  },
  colorBlock: {
    width: '100%',
    height: '100%',
    minWidth: '100%',
    minHeight: '100%',
    aspectRatio: '45 / 55'
  }
})

Card.propTypes = {
  cardContent: PropTypes.shape({
    productImage: PropTypes.object,
    image: PropTypes.object,
    title: PropTypes.string,
    name: PropTypes.string,
    productSpecSheet: PropTypes.object,
    slug: PropTypes.object
  }).isRequired,
  isFocused: PropTypes.bool,
  onClick: PropTypes.func // New prop for handling click events
}

export default Card
